<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ComputedRef } from 'vue'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/tooltip'
import { useUserStore } from '../authentication/user.store'
import { DELETED_USER_NAME } from '../authentication/user.utils'
import type { LibraryFolder } from './library_types'

interface Props {
    userId: string,
    avatarSize?: 'xs' | 'sm' | 'md' | 'lg'
    folder?: LibraryFolder,
    rightMargin?: boolean,
    disableTooltip?: boolean,
    deleted?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    userId: '',
    avatarSize: 'md',
    folder: undefined,
    rightMargin: true,
    disableTooltip: false,
    deleted: false
})

const avatarSize: ComputedRef<string> = computed(() => {
  switch (props.avatarSize) {
    case 'xs':
      return 'w-[18px] h-[18px]'
    case 'sm':
      return 'w-6 h-6' // 24x24
    case 'md':
      return 'w-7 h-7' // 28x28
    case 'lg':
      return 'w-8 h-8' // 32x32
    default:
      return ''
  }
})

const fontSizeClass: ComputedRef<string> = computed(() => {
  if (props.avatarSize === 'xs') {
    return 'text-sm leading-none'
  }
  return 'text-sm'
})

const userStore = useUserStore()
const { getFolderMemberInfo } = storeToRefs(userStore)
const allFolderMembers = computed(() => getFolderMemberInfo.value(props.folder))
const deletedMember = {
  initials: '?',
  name: DELETED_USER_NAME,
  email: '',
  isCurrentUser: false
} as const
const unknownMember = {
  initials: '?',
  name: 'Unknown user',
  email: '',
  isCurrentUser: false
} as const
const thisFolderMember = computed(() => {
  if (props.deleted) {
    return deletedMember
  }
  return allFolderMembers.value?.[props.userId] ?? unknownMember
})
const thisFolderMemberName = computed(() => thisFolderMember.value.name)
const thisFolderMemberEmail = computed(() => thisFolderMember.value.email)
const thisFolderMemberInitials = computed(() => thisFolderMember.value.initials)
const thisFolderMemberIsCurrentUser = computed(() => thisFolderMember.value.isCurrentUser)
// Must match the visible TooltipContent text verbatim: reka-ui's internal
// role="tooltip" node is aria-hidden (see TooltipContentImpl.js), so this
// trigger's own accessible name is the only reliable way screen readers get
// the tooltip's content (WCAG 4.1.2, custom-tooltip).
const ariaLabel = computed(() => {
    if (props.disableTooltip) {
      return ''
    }
    const nameLine = thisFolderMemberIsCurrentUser.value
      ? `${thisFolderMemberName.value} (Me)`
      : thisFolderMemberName.value
    return thisFolderMemberEmail.value ? `${nameLine} ${thisFolderMemberEmail.value}` : nameLine
})
const stateClasses = computed(() => {
    const classes: string[] = [avatarSize.value, fontSizeClass.value]
    if (thisFolderMemberIsCurrentUser.value) {
      classes.push('me')
    } else {
      classes.push('other')
    }
    if (!props.disableTooltip) {
      classes.push('interactive')
    }
    return classes
})

</script>

<template>
    <TooltipProvider
        :delayDuration="200">
        <div v-if="props.disableTooltip"
            data-testid="avatar"
            aria-hidden="true"
            class="rounded-full flex items-center justify-center outline-none fs-exclude avatar"
            :class="[{ 'mr-2': rightMargin && props.avatarSize !== 'xs' }, stateClasses]">
            {{ thisFolderMemberInitials }}
        </div>
        <Tooltip v-else side="bottom">
        <TooltipTrigger asChild>
            <button
                type="button"
                data-testid="avatar"
                :aria-label="ariaLabel"
                class="rounded-full flex items-center justify-center outline-none fs-exclude avatar"
                :class="[{ 'mr-2': rightMargin && props.avatarSize !== 'xs' }, stateClasses]">
                {{ thisFolderMemberInitials }}
            </button>
        </TooltipTrigger>
        <TooltipContent class="max-w-[300px] tooltip-content fs-exclude" :disablePortal="true">
            <p>{{ thisFolderMemberName }} {{ thisFolderMemberIsCurrentUser ? '(Me) ' : '' }}</p>
            <p v-if="thisFolderMemberEmail">{{ thisFolderMemberEmail }}</p>
        </TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>

<style scoped>
.avatar {
  @apply outline-none;

  &.interactive {
    @apply cursor-help focus:ring-2 focus:ring-offset-2;

    &.me {
      @apply hover:bg-blue-500 focus:bg-blue-500 focus:ring-blue-500;
    }
    &.other {
      @apply hover:bg-gray-300 focus:bg-gray-300 focus:ring-blue-400;
    }
  }

  &.me {
    @apply bg-blue-400 text-white;
  }
  &.other {
    @apply text-blue-400 bg-white border border-blue-400;
  }
}
</style>
